import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Select,
  Spinner,
  Textarea,
  makeStyles,
} from '@fluentui/react-components';
import { useEffect, useRef, useState } from 'react';
import { useConfigurationStorage } from '@data';
import { formatDebugInfo, getDebugInfo, probeNetwork } from '@utils-global';
import {
  AuthMethod,
  getAuthMethodOverride,
  getIsAuthMethodOverrideAllowed,
  setAuthMethodOverride,
} from '../../auth/authMethod';

const useStyles = makeStyles({
  surface: {
    width: 'calc(100% - 2rem)',
    maxWidth: 'calc(100% - 2rem)',
  },
  textarea: {
    width: '100%',
  },
  field: {
    marginBottom: '12px',
  },
  textareaInner: {
    maxHeight: '50vh',
    fontFamily: 'monospace',
    fontSize: '12px',
  },
});

export const DebugInfoOverlay = () => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [content, setContent] = useState('');
  const probing = useRef(false);

  useEffect(() => {
    const handler = async (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.shiftKey && event.key.toLowerCase() === 'd') {
        event.preventDefault();

        // Guard against overlapping probes: repeated shortcut presses while a
        // probe is in flight would let an early `finally` clear the loading gate
        // (exposing stale content) before the latest probe resolves.
        if (probing.current) return;
        probing.current = true;

        setLoading(true);
        setOpen(true);

        try {
          const { configuration } = useConfigurationStorage.getState();
          const network = await probeNetwork();

          setContent(
            formatDebugInfo(
              getDebugInfo({
                config: configuration?.data,
                network,
              }),
            ),
          );
        } catch (error) {
          setContent(
            `Failed to gather debug info: ${
              error instanceof Error ? error.message : String(error)
            }`,
          );
        } finally {
          setLoading(false);
          probing.current = false;
        }
      }
    };

    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  return (
    <Dialog open={open} onOpenChange={(_, data) => setOpen(data.open)}>
      <DialogSurface className={styles.surface}>
        <DialogBody>
          <DialogTitle>Debug</DialogTitle>
          <DialogContent>
            {getIsAuthMethodOverrideAllowed() && (
              <Field label="Force auth method" className={styles.field}>
                <Select
                  value={getAuthMethodOverride() ?? ''}
                  onChange={(_, data) => setAuthMethodOverride(data.value)}
                >
                  <option value="">Auto</option>
                  <option value={AuthMethod.NAA_INTERACTIVE}>NAA</option>
                  <option value={AuthMethod.SSO}>SSO</option>
                  <option value={AuthMethod.DIALOG}>Dialog</option>
                </Select>
              </Field>
            )}
            {loading ? (
              <Spinner label="Loading…" />
            ) : (
              <Textarea
                readOnly
                value={content}
                className={styles.textarea}
                textarea={{ className: styles.textareaInner, rows: 18 }}
                onFocus={(event) => event.target.select()}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button appearance="primary" onClick={() => setOpen(false)}>
              Close
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
